import { isEmpty } from 'lodash';
import { all, call, fork, put, select } from 'redux-saga/effects';
import {
  fetchAnimalOptions,
  fetchGoodsTypes,
  fetchSailings,
  fetchSenderReceiverCountries,
  fetchVehicleTypeOptionsCached,
} from '../../../common/api';
import {
  getGoodsItemsForLivestockType,
  getGoodsItemsWithoutAnimals,
  getRegistrationNumberAlertGoodsItem,
  getTotalWeightFromGoodsItems,
} from '../../../common/goods/goodsUtils';
import type { Route } from '../../../common/graphql/fragments/gql/Route';
import type { Sailing } from '../../../common/graphql/fragments/gql/Sailing';
import type { VehicleType } from '../../../common/graphql/fragments/gql/VehicleType';
import { animalDropdownNoOption } from '../../../common/graphql/useAnimalTypesOptions';
import { customsClassificationDropdownEmptyOption } from '../../../common/graphql/useCustomsClassificationsOptions';
import { transformCountryOptions } from '../../../common/graphql/useSenderReceiverCountryOptions';
import {
  handleVehicleRegNoRequest,
  type RegistrationNumberAlertStateChange,
} from '../../../common/registrationNumberAlert/sagas/registrationNumberAlertsSaga';
import { hasSenderReceiverCountries } from '../../../common/update/utils/hasSenderReceiverCountries';
import { isAnimalLivestockType } from '../../../common/utils/animalsUtils';
import { shouldClearUkCustomsClassification } from '../../../common/utils/customsClassificationUtil';
import { getNewBookableSailing } from '../../../common/utils/sailingUtils';
import type { ResolveGenerator } from '../../../types/ResolveGenerator';
import { TravelledVehicleType } from '../../update-form/registrationNumberAlert';
import {
  type BookingCandidateId,
  type CreateFormIdVariant,
  type CreateFormSetAllSpecificValuesAction,
  type CreateFormUpdateCommonValuesAction,
  type CreateFormUpdateSpecificValuesAction,
  setAllSpecificValues,
  setSpecificValues,
  updateAllSpecificValues,
  updateCommonValues,
  updateSpecificValues,
} from '../createFormActions';
import type { CreateCandidate, CreateFormState } from '../createFormReducer';
import { enableExtendedMode } from '../createMetaActions';
import { getCreateFormState, getCreateFormStateCandidate } from '../createReducer';
import { getNumberOfDrivers } from '../utils/transformCreateBookingResults';

function* fetchSenderReceiverCountriesForRoute(route: Route) {
  const countries: Awaited<ReturnType<typeof fetchSenderReceiverCountries>> = yield call(
    fetchSenderReceiverCountries,
  );

  const countryOptions = transformCountryOptions(countries, true);

  return {
    senderCountry: countryOptions.find(option => option.value === route.departureCountryCode)?.data,
    receiverCountry: countryOptions.find(option => option.value === route.arrivalCountryCode)?.data,
  };
}

function* setUkCustomsClassification(
  formId: CreateFormIdVariant,
  departureDate: string,
  departureTime: string,
  route: Route,
  candidates: CreateCandidate[],
) {
  yield all(
    candidates
      .filter(c =>
        shouldClearUkCustomsClassification(
          departureDate,
          departureTime,
          route,
          c.customsClassification,
        ),
      )
      .map((_c, index) =>
        put(
          setSpecificValues(formId, index, {
            customsClassification: customsClassificationDropdownEmptyOption.data,
          }),
        ),
      ),
  );
}

function* fetchAndUpdateVehicleType(
  formId: CreateFormIdVariant,
  customerNumber: number,
  routeCode: string,
  candidates: CreateCandidate[],
) {
  const vehicleTypes: Awaited<ReturnType<typeof fetchVehicleTypeOptionsCached>> = yield call(
    fetchVehicleTypeOptionsCached,
    customerNumber,
    routeCode,
  );

  const actions = candidates.map((candidate, index) => {
    const vehicleType = candidate.vehicleType;
    if (vehicleType) {
      const vehicleTypeOnRoute = vehicleTypes.find(v => v.code === vehicleType.code);
      return updateSpecificValues(formId, index, {
        vehicleType: vehicleTypeOnRoute || vehicleTypes[0],
      });
    } else {
      return undefined;
    }
  });

  for (const action of actions) {
    if (action) {
      yield put(action);
    }
  }
}

function* fetchAndUpdateSailing(
  formId: CreateFormIdVariant,
  routeCode: string,
  departureDate: string,
  withDepartureTime: { departureTime: string } | undefined,
) {
  if (routeCode && departureDate) {
    const sailings: Awaited<ReturnType<typeof fetchSailings>> = yield call(
      fetchSailings,
      routeCode,
      departureDate,
    );

    const sailing = getNewBookableSailing(sailings, withDepartureTime);

    yield put(updateCommonValues(formId, { sailing }));
  }
}

function* setSpecificValuesFromVehicleType(
  formId: CreateFormIdVariant,
  id: BookingCandidateId,
  candidate: CreateCandidate,
  vehicleType: VehicleType,
) {
  const { defaultWidth, defaultHeight, defaultLength, defaultTradeWeight } = vehicleType;

  const defaultNoOfDrivers = getNumberOfDrivers(vehicleType);

  const values: Partial<CreateCandidate> = {
    height: defaultHeight,
    length: defaultLength,
    numberOfDrivers: defaultNoOfDrivers,
    tradeWeight: defaultTradeWeight,
    width: defaultWidth,
    livestockType: vehicleType.useAnimals ? candidate.livestockType : animalDropdownNoOption.data,
    ...(!vehicleType.useAnimals ? { goods: getGoodsItemsWithoutAnimals(candidate.goods) } : {}),
  };

  yield put(setSpecificValues(formId, id, values));
}

function* setAllValuesFromVehicleType(formId: CreateFormIdVariant, vehicleType: VehicleType) {
  const { defaultWidth, defaultHeight, defaultLength, defaultTradeWeight } = vehicleType;

  const defaultNoOfDrivers = getNumberOfDrivers(vehicleType);

  const values: Partial<CreateCandidate> = {
    height: defaultHeight,
    length: defaultLength,
    numberOfDrivers: defaultNoOfDrivers,
    tradeWeight: defaultTradeWeight,
    width: defaultWidth,
  };

  yield put(setAllSpecificValues(formId, values));

  // If the vehicle type does not support animals, we need to remove potential animal goods on each booking.
  // We also need to set the livestock type to "No animals" for each booking.
  if (!vehicleType.useAnimals) {
    const state: CreateFormState = yield select(getCreateFormState(formId));

    yield all(
      state.bookings.map((booking, index) =>
        put(
          setSpecificValues(formId, index, {
            goods: getGoodsItemsWithoutAnimals(booking.goods),
            livestockType: animalDropdownNoOption.data,
          }),
        ),
      ),
    );
  }
}

export function* handleUpdateCommonValues(action: CreateFormUpdateCommonValuesAction) {
  const { formId, values } = action.payload;

  const state: CreateFormState = yield select(getCreateFormState(formId));

  yield put(updateAllSpecificValues(formId, { registrationNumberAlert: null }));

  // If customer or route has been changed
  if (values.route || values.customer) {
    const customer = values.customer ?? state.customer;
    const route = values.route ?? state.route;

    if (customer && route) {
      yield* fetchAndUpdateVehicleType(formId, customer.custNo, route.code, state.bookings);

      if (customer.isCashCustomer) {
        const { senderCountry, receiverCountry } =
          yield* fetchSenderReceiverCountriesForRoute(route);

        yield all(
          state.bookings
            .filter(hasSenderReceiverCountries)
            .map((_c, index) =>
              put(setSpecificValues(formId, index, { senderCountry, receiverCountry })),
            ),
        );
      } else {
        yield put(
          setAllSpecificValues(formId, {
            senderCountry: undefined,
            receiverCountry: undefined,
          }),
        );
      }
    }
  }

  // If route has been changed
  if (values.route && state.customer && state.departureDate) {
    yield fetchAndUpdateVehicleType(
      action.payload.formId,
      state.customer.custNo,
      values.route.code,
      state.bookings,
    );

    yield fetchAndUpdateSailing(
      action.payload.formId,
      values.route.code,
      state.departureDate,
      state.sailing ?? undefined,
    );

    yield put(
      setAllSpecificValues(formId, {
        importReference: '',
        exportReference: '',
      }),
    );

    if (!values.route.useCargoWeight || !values.route.useSenderReceiverCountry) {
      yield put(
        setAllSpecificValues(formId, {
          senderCountry: undefined,
          receiverCountry: undefined,
        }),
      );
    }

    if (!values.route.useLivestockType) {
      yield all(
        state.bookings.map((_c, index) =>
          put(
            setSpecificValues(formId, index, {
              hasAnimals: false,
              livestockType: animalDropdownNoOption.data,
              goods: getGoodsItemsWithoutAnimals(state.bookings[index].goods),
            }),
          ),
        ),
      );
    }
  }

  // If departure date has been changed
  if ('departureDate' in values && state.route) {
    let sailing: Sailing | null | undefined = null;

    if (values.departureDate) {
      const sailings: Awaited<ReturnType<typeof fetchSailings>> = yield call(
        fetchSailings,
        state.route.code,
        values.departureDate,
      );

      sailing = getNewBookableSailing(sailings, state.sailing);

      if (sailing) {
        yield fork(
          setUkCustomsClassification,
          formId,
          values.departureDate,
          sailing.departureTime,
          state.route,
          state.bookings,
        );
      }
    }

    yield put(updateCommonValues(formId, { sailing }));
  }
}

const transformRegistrationNumberAlertToSpecific = (
  alert: RegistrationNumberAlertStateChange,
  candidate: CreateCandidate,
): Partial<CreateCandidate> => {
  return {
    hasHazardousGoods:
      alert.hazardousGoods !== undefined ? alert.hazardousGoods : candidate.hasHazardousGoods,
    height: alert.height || candidate.height,
    length: alert.length || candidate.length,
    loadingListMessage: alert.loadingListMessage || candidate.loadingListMessage,
    registrationNumberAlert: {
      appliedArrivalNotepad: alert.registrationNumberAlert.appliedArrivalNotepad,
      appliedGoods: alert.registrationNumberAlert.appliedGoods,
      appliedHazardousGoods: alert.registrationNumberAlert.appliedHazardousGoods,
      appliedHeight: alert.registrationNumberAlert.appliedHeight,
      appliedLength: alert.registrationNumberAlert.appliedLength,
      appliedLoadingListMessage: alert.registrationNumberAlert.appliedLoadingListMessage,
      appliedTravelledVehicle: alert.registrationNumberAlert.appliedTravelledVehicle,
      result: alert.registrationNumberAlert.result,
      travelledVehicleType: alert.registrationNumberAlert.travelledVehicleType,
      vehicleType: alert.registrationNumberAlert.vehicleType,
    },
    vehicleType: alert.vehicleType || candidate.vehicleType,
    goods: alert.goodsWeight
      ? getRegistrationNumberAlertGoodsItem(alert.goodsWeight)
      : candidate.goods,
    empty:
      alert.registrationNumberAlert.appliedArrivalNotepad && alert.goodsWeight
        ? false
        : candidate.empty,
  };
};

function* updateValuesFromVehicleReg(
  formId: CreateFormIdVariant,
  id: BookingCandidateId,
  state: CreateFormState,
  candidate: CreateCandidate,
  vehicleRegNo: string,
  travelledVehicleType: TravelledVehicleType,
) {
  if (!state.customer || !state.route) {
    return;
  }

  const registrationNumberAlertResult: ResolveGenerator<
    ReturnType<typeof handleVehicleRegNoRequest>
  > = yield call(
    handleVehicleRegNoRequest,
    state.customer.custNo,
    state.route.code,
    vehicleRegNo,
    travelledVehicleType,
    getTotalWeightFromGoodsItems(candidate.goods),
    candidate.hasHazardousGoods,
    candidate.vehicleType,
    candidate.height,
    candidate.length,
    state.bookings[id].loadingListMessage,
  );

  if (registrationNumberAlertResult) {
    yield put(
      setSpecificValues(
        formId,
        id,
        transformRegistrationNumberAlertToSpecific(registrationNumberAlertResult, candidate),
      ),
    );

    if (
      registrationNumberAlertResult.registrationNumberAlert.appliedArrivalNotepad ||
      registrationNumberAlertResult.registrationNumberAlert.appliedHeight ||
      registrationNumberAlertResult.registrationNumberAlert.appliedLength
    ) {
      yield put(enableExtendedMode(formId));
    }
  } else if (
    candidate.registrationNumberAlert &&
    candidate.registrationNumberAlert.travelledVehicleType === travelledVehicleType
  ) {
    yield put(setSpecificValues(formId, id, { registrationNumberAlert: null }));
  }
}

export function* handleUpdateSpecificValues(action: CreateFormUpdateSpecificValuesAction) {
  const { formId, values, id } = action.payload;
  // TODO: Consider making this function only act on one candidate. Now we let some sagas decide whether they should apply for
  //       all candidates or not by looking at id === null

  const state: CreateFormState = yield select(getCreateFormState(formId));
  const candidate: CreateCandidate | undefined = yield select(
    getCreateFormStateCandidate(formId),
    id,
  );

  if (!candidate) {
    return;
  }

  if (values.vehicleType) {
    yield setSpecificValuesFromVehicleType(formId, id, candidate, values.vehicleType);
  }

  if (values.pluginTemperature) {
    yield put(
      setSpecificValues(formId, id, { hasElectricalPlugin: values.pluginTemperature !== '' }),
    );
  }

  if (values.vehicleReg !== undefined) {
    yield updateValuesFromVehicleReg(
      formId,
      id,
      state,
      candidate,
      values.vehicleReg,
      TravelledVehicleType.Vehicle,
    );
  }

  if (values.trailerReg !== undefined) {
    yield updateValuesFromVehicleReg(
      formId,
      id,
      state,
      candidate,
      values.trailerReg,
      TravelledVehicleType.Trailer,
    );
  }

  if (values.hasAnimals) {
    const animalOptions: Awaited<ReturnType<typeof fetchAnimalOptions>> =
      yield call(fetchAnimalOptions);
    yield put(setSpecificValues(formId, id, { livestockType: animalOptions[0].data }));
  }

  if ('livestockType' in values && values.livestockType) {
    const goodsTypes: Awaited<ReturnType<typeof fetchGoodsTypes>> = yield call(fetchGoodsTypes);
    const goods = getGoodsItemsForLivestockType(goodsTypes, candidate.goods, values.livestockType);

    yield put(
      setSpecificValues(formId, id, {
        hasAnimals: isAnimalLivestockType(values.livestockType),
        goods,
      }),
    );
  }

  if ('goods' in values) {
    if (isEmpty(values.goods)) {
      yield put(
        setSpecificValues(formId, id, {
          senderCountry: undefined,
          receiverCountry: undefined,
        }),
      );
    } else {
      if (state.customer && state.customer.isCashCustomer && state.route) {
        const { senderCountry, receiverCountry } = yield* fetchSenderReceiverCountriesForRoute(
          state.route,
        );

        yield put(
          setSpecificValues(formId, id, {
            senderCountry,
            receiverCountry,
          }),
        );
      }
    }
  }
}

export function* handleUpdateAllSpecificValues(action: CreateFormSetAllSpecificValuesAction) {
  const { formId, values } = action.payload;

  if (values.vehicleType) {
    yield setAllValuesFromVehicleType(formId, values.vehicleType);
  }
}
